/**
 * WorkQueueFilterContext
 * Context for managing work queue filter state (search, tag filter, sort)
 */

import { createContext, useContext, useState, useCallback, useMemo, useDeferredValue } from 'react'
import type { ReactNode } from 'react'
import type { IssueTagType } from '../../../domain'
import type { WorkQueueSortBy, WorkQueueSortDirection, TagCounts } from '../types/workQueueFilters'

export interface FilterState {
  searchQuery: string
  deferredSearchQuery: string
  activeTagFilter: IssueTagType | null
  sortBy: WorkQueueSortBy
  sortDirection: WorkQueueSortDirection
  tagCounts: TagCounts
}

export interface FilterActions {
  setSearchQuery: (query: string) => void
  setActiveTagFilter: (tagType: IssueTagType | null) => void
  setSortBy: (sortBy: WorkQueueSortBy) => void
  setSortDirection: (direction: WorkQueueSortDirection) => void
  setSort: (sortBy: WorkQueueSortBy, sortDirection: WorkQueueSortDirection) => void
  setTagCounts: (counts: TagCounts) => void
}

type WorkQueueFilterContextValue = FilterState & FilterActions

const WorkQueueFilterContext = createContext<WorkQueueFilterContextValue | null>(null)

const emptyTagCounts: TagCounts = {
  QUICK_WIN: 0,
  BLOCKED: 0,
  OVERDUE: 0,
  FAMILIAR: 0,
  RECOMMENDED: 0,
  HIGH_IMPACT: 0,
  ESCALATED: 0,
}

export interface WorkQueueFilterProviderProps {
  children: ReactNode
}

export function WorkQueueFilterProvider({ children }: WorkQueueFilterProviderProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTagFilter, setActiveTagFilter] = useState<IssueTagType | null>(null)
  const [sortBy, setSortBy] = useState<WorkQueueSortBy>('createdAt')
  const [sortDirection, setSortDirection] = useState<WorkQueueSortDirection>('desc')
  const [tagCounts, setTagCounts] = useState<TagCounts>(emptyTagCounts)

  // Use deferred value for search to avoid blocking during typing
  const deferredSearchQuery = useDeferredValue(searchQuery)

  const setSort = useCallback((newSortBy: WorkQueueSortBy, newSortDirection: WorkQueueSortDirection) => {
    setSortBy(newSortBy)
    setSortDirection(newSortDirection)
  }, [])

  const value = useMemo<WorkQueueFilterContextValue>(
    () => ({
      searchQuery,
      deferredSearchQuery,
      activeTagFilter,
      sortBy,
      sortDirection,
      tagCounts,
      setSearchQuery,
      setActiveTagFilter,
      setSortBy,
      setSortDirection,
      setSort,
      setTagCounts,
    }),
    [searchQuery, deferredSearchQuery, activeTagFilter, sortBy, sortDirection, tagCounts, setSort]
  )

  return (
    <WorkQueueFilterContext.Provider value={value}>
      {children}
    </WorkQueueFilterContext.Provider>
  )
}

export function useWorkQueueFilter(): WorkQueueFilterContextValue {
  const context = useContext(WorkQueueFilterContext)
  if (!context) {
    throw new Error('useWorkQueueFilter must be used within a WorkQueueFilterProvider')
  }
  return context
}
